import { computeUtilization, parseRawAmount } from '../../../../utils/parsing'
import { exchangeRateAccumulator } from '../../accumulator'
import { LENDER_MODE_NO_MODE } from '../../../../assets'
import { createMarketUid } from '../../../../utils'
import { lenderShortName } from '../../../../utils/marketName'
import { parseTectonicMarketData, getTectonicRates } from '../getters/tectonic'
import {
  PRICE_DECIMALS,
  RESERVE_MANTISSA_DECIMALS,
  ConvertEntryOpts,
  resolveMarketPrice,
} from './types'
import { DEFAULT_CLOSE_FACTOR } from './risk'
import { cashLiquidity } from './liquidity'

// Tectonic returns all markets via getMarketsData() in a single batch call.
// Raw amounts are NOT price-scaled (unlike Takara), so we compute USD values
// from the off-chain price and underlying decimals.
export function convertTectonicEntry(
  opts: ConvertEntryOpts & { chainId: string },
) {
  const { entryRaw, asset, chainId, lender, prices, additionalYields, cToken } =
    opts

  const info = parseTectonicMarketData(entryRaw)
  const rates = getTectonicRates(info, chainId)
  const price = resolveMarketPrice(prices, asset, chainId)
  const decs = asset.decimals as number

  const collateralFactor = Number(
    parseRawAmount(info.collateralFactor.toString(), RESERVE_MANTISSA_DECIMALS),
  )

  // Tectonic exposes a per-market liquidationPenalty as a WAD incentive
  // mantissa (e.g. 1.08e18). Convert to a fraction: mantissa/1e18 - 1.
  const liquidationPenalty = Math.max(
    Number(parseRawAmount(info.liquidationPenalty.toString(), PRICE_DECIMALS)) -
      1,
    0,
  )

  // exchangeRate is scaled by 1e(10 + underlyingDecimals) for 8-dec tTokens.
  // Dividing by 1e18 gives underlyingPerCToken * 1e(decimal - 8).
  const exchangeRateCurrent = Number(
    parseRawAmount(info.exchangeRate.toString(), PRICE_DECIMALS),
  )

  // totalSupply is raw cToken supply; dividing by 1e(underlyingDecimals) then
  // multiplying by exchangeRate/1e18 gives human-readable underlying amount.
  const totalSupplyUnderlying =
    Number(parseRawAmount(info.totalSupply.toString(), decs)) *
    exchangeRateCurrent

  const totalDebt = Number(parseRawAmount(info.totalBorrow.toString(), decs))

  // the Tectonic lens names `getCash()` `availableLiquidity`
  const liquidity =
    cashLiquidity(info.availableLiquidity, decs) ??
    totalSupplyUnderlying - totalDebt

  const poolId = asset.address
  const marketUid = createMarketUid(chainId, lender, cToken)

  return {
    marketUid,
    name: lenderShortName(lender) + ' ' + (asset?.symbol ?? ''),
    poolId,
    asset,
    accumulator: exchangeRateAccumulator(info.exchangeRate),
    depositRate: rates.depositRate,
    variableBorrowRate: rates.variableBorrowRate,
    stableBorrowRate: 0,
    totalLiquidity: liquidity,
    borrowLiquidity: liquidity,
    totalLiquidityUSD: liquidity * price,
    borrowLiquidityUSD: liquidity * price,
    totalDebt,
    totalDebtUSD: totalDebt * price,
    totalDeposits: totalSupplyUnderlying,
    totalDepositsUSD: totalSupplyUnderlying * price,
    totalDebtStable: 0,
    totalDebtStableUSD: 0,
    utilization: computeUtilization(totalSupplyUnderlying, totalDebt),
    intrinsicYield: additionalYields?.intrinsicYields?.[asset.assetGroup] ?? 0,
    config: {
      [LENDER_MODE_NO_MODE]: {
        category: LENDER_MODE_NO_MODE,
        borrowCollateralFactor: collateralFactor,
        collateralFactor,
        borrowFactor: 1,
        liquidationPenalty,
        closeFactor: DEFAULT_CLOSE_FACTOR,
        collateralDisabled: !info.asCollateralEnabled,
        debtDisabled: false,
      },
    },
    closeFactor: DEFAULT_CLOSE_FACTOR,
    hasStable: false,
    underlying: asset.address,
    isActive: info.asCollateralEnabled,
    isFrozen: false,
    rewards: [],
    collateralActive: info.asCollateralEnabled,
    borrowingEnabled: info.asCollateralEnabled,
    depositsEnabled: true,
    params: {
      metadata: {
        cToken,
        exchangeRate: info.exchangeRate.toString(),
        cTokenDecimals: info.ttokenDecimal,
      },
    },
  }
}
